import type { ErrorLike } from "@apollo/client";

import { useMemo } from "react";

import { useGenerateOrGetFolderReportMutation } from "@/graphql/folders/folders.hooks";
import { GraphqlMappedError } from "@/services/graphqlErrorService";

interface IUseGenerateOrGetFolderReportOptions {
  onCompleted?: () => void;
  onError?: (error: GraphqlMappedError) => void;
}

interface IUseGenerateOrGetFolderReportReturnedValue {
  generateOrGetFolderReport: (folderId: number) => Promise<
    | {
        status: string;
        reportKey: string;
        downloadUrl?: string | null;
        message?: string | null;
      }
    | undefined
  >;
  error?: ErrorLike;
  loading: boolean;
}

const useGenerateOrGetFolderReport = ({
  onCompleted,
  onError,
}: IUseGenerateOrGetFolderReportOptions): IUseGenerateOrGetFolderReportReturnedValue => {
  const [generateOrGetFolderReport, { loading, error }] =
    useGenerateOrGetFolderReportMutation({
      context: { errorType: "local" },
      onCompleted: () => onCompleted?.(),
      onError: (e) => onError?.(GraphqlMappedError.from(e)),
    });

  return useMemo(
    () => ({
      generateOrGetFolderReport: async (folderId: number) => {
        const { data } = await generateOrGetFolderReport({
          variables: {
            folderId,
          },
        });
        return data?.generateOrGetFolderReport;
      },
      loading,
      error,
    }),
    [generateOrGetFolderReport, error, loading]
  );
};

export default useGenerateOrGetFolderReport;
